// src/components/RRRReports/RrrReports.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "../../css/MSLPob.css";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import CustomButton from "../../pages/CustomButton";
import Loader from "../../dist/Loader";
import XLSX from "xlsx-js-style";

// Persistent storage outside component
const persistentState = {
  states: [],
  divisions: [{ id: "All", name: "All" }],
  employees: [{ id: "All", first_name: "All", last_name: "" }],
  selectedState: "All",
  selectedDivision: "All",
  selectedEmployee: "All",
  retailerType: "All",
  activeStatus: "All",
  startMonth: "",
  endMonth: "",
  financialYear: "",
  tableData: [],
  showTable: false,
  isInitialized: false,
};

// popup offset counter for staggered positioning
let _popupCounter = 0;

const openPopup = (url, opts = {}) => {
  // default popup size
  const width = opts.width || 1000;
  const height = opts.height || 700;

  // stagger popups so they don't overlap exactly
  const leftBase = opts.leftBase || 50;
  const topBase = opts.topBase || 50;
  const leftOffset = (_popupCounter * 40) % 800; // wrap offset
  const topOffset = (_popupCounter * 30) % 600;

  const left = leftBase + leftOffset;
  const top = topBase + topOffset;

  _popupCounter += 1;

  const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
  // use unique window name so multiple popups open
  const winName = `popup_${Date.now()}_${_popupCounter}`;
  window.open(url, winName, features);
};
const FY_MONTH_KEYS = [
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
  "jan",
  "feb",
  "mar",
];

const getSelectedMonthKeys = (startMonth, endMonth, financialYear) => {
  if (!startMonth || !endMonth || !financialYear) return FY_MONTH_KEYS;

  const [fyStart] = financialYear.split("-").map(Number);

  const parse = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    return { year: y, month: m };
  };

  const s = parse(startMonth);
  const e = parse(endMonth);

  const months = [];

  FY_MONTH_KEYS.forEach((key, idx) => {
    const monthIndex = idx < 9 ? idx + 4 : idx - 8; // Apr=4 ... Mar=3
    const year = monthIndex >= 4 ? fyStart : fyStart + 1;

    if (
      (year > s.year || (year === s.year && monthIndex >= s.month)) &&
      (year < e.year || (year === e.year && monthIndex <= e.month))
    ) {
      months.push(key);
    }
  });

  return months;
};

const RrrReports = () => {
  // State hooks
  const [states, setStates] = useState(persistentState.states);
  const [divisions, setDivisions] = useState(persistentState.divisions);
  const [employees, setEmployees] = useState(persistentState.employees);
  const [selectedState, setSelectedState] = useState(
    persistentState.selectedState,
  );
  const [selectedDivision, setSelectedDivision] = useState(
    persistentState.selectedDivision,
  );
  const [selectedEmployee, setSelectedEmployee] = useState(
    persistentState.selectedEmployee,
  );
  const [retailerType, setRetailerType] = useState(
    persistentState.retailerType,
  );
  const [activeStatus, setActiveStatus] = useState(
    persistentState.activeStatus,
  );
  const [startMonth, setStartMonth] = useState(persistentState.startMonth);
  const [endMonth, setEndMonth] = useState(persistentState.endMonth);
  const [financialYear, setFinancialYear] = useState(
    persistentState.financialYear,
  );
  const [tableData, setTableData] = useState(persistentState.tableData);
  const [showTable, setShowTable] = useState(persistentState.showTable);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    state: false,
    employee: false,
  });

  // Edit state
  const [editingRow, setEditingRow] = useState(null);
  const [editStatus, setEditStatus] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Month configuration
  const monthKeys = [
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
    "jan",
    "feb",
    "mar",
  ];
  const monthDisplay = [
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    "Jan",
    "Feb",
    "Mar",
  ];

  // Helper to get current financial year
  const getCurrentFinancialYear = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (currentMonth >= 4) {
      return `${currentYear}-${currentYear + 1}`;
    } else {
      return `${currentYear - 1}-${currentYear}`;
    }
  };
  const visibleMonthKeys = useMemo(
    () => getSelectedMonthKeys(startMonth, endMonth, financialYear),
    [startMonth, endMonth, financialYear],
  );

  const visibleMonthDisplay = monthDisplay.filter((_, i) =>
    visibleMonthKeys.includes(monthKeys[i]),
  );
  const footerTotals = useMemo(() => {
    if (!tableData || tableData.length === 0) return null;

    const totals = {
      prevFyAvg: 0,
      currFyAvg: 0,
      totalVisits: 0,
      totalPob: 0,
      monthTotals: {},
    };

    // Initialize month totals
    visibleMonthKeys.forEach((m) => {
      totals.monthTotals[m] = {
        visit: 0,
        pob: 0,
      };
    });

    tableData.forEach((item) => {
      totals.prevFyAvg += Number(item.fy_avg_prev || 0);
      totals.currFyAvg += Number(item.fy_avg_curr || 0);

      visibleMonthKeys.forEach((m) => {
        const visit = Number(item[`${m}_visit`] || 0);
        const pob = Number(item[`${m}_pob`] || 0);

        totals.monthTotals[m].visit += visit;
        totals.monthTotals[m].pob += pob;

        totals.totalVisits += visit;
        totals.totalPob += pob;
      });
    });

    return totals;
  }, [tableData, visibleMonthKeys]);

  // Helper to get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}`;
  };

  // Generate financial years
  const getFinancialYears = () => {
    const currentYear = new Date().getFullYear();
    let years = [];
    for (let y = 2024; y <= currentYear + 1; y++) {
      years.push(`${y}-${y + 1}`);
    }
    return years;
  };

  // Fetch employees based on state and division
  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    if (!stateId || stateId === "All") {
      const updatedEmployees = [
        { id: "All", first_name: "All", last_name: "" },
      ];
      setEmployees(updatedEmployees);
      persistentState.employees = updatedEmployees;
      return;
    }

    try {
      let url = `${BASE_URL}/api/employees?state_id=${stateId}`;
      if (divisionName && divisionName !== "All") {
        url += `&division=${encodeURIComponent(divisionName)}`;
      }
      const res = await axios.get(url, { headers: getAuthHeaders() });
      const updatedEmployees = [
        { id: "All", first_name: "All", last_name: "" },
        ...res.data,
      ];
      setEmployees(updatedEmployees);
      persistentState.employees = updatedEmployees;

      // Auto-select "All" for employee when state/division changes
      setSelectedEmployee("All");
      persistentState.selectedEmployee = "All";
    } catch (err) {
      const updatedEmployees = [
        { id: "All", first_name: "All", last_name: "" },
      ];
      setEmployees(updatedEmployees);
      persistentState.employees = updatedEmployees;
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    const fetchStates = async () => {
      if (!persistentState.isInitialized) {
        try {
          const response = await axios.get(`${BASE_URL}/api/states`, {
            headers: getAuthHeaders(),
          });
          const updatedStates = [{ id: "All", name: "All" }, ...response.data];
          setStates(updatedStates);
          persistentState.states = updatedStates;

          // Initialize dates if not set
          if (!persistentState.startMonth) {
            const currentMonth = getCurrentMonth();
            setStartMonth(currentMonth);
            persistentState.startMonth = currentMonth;
          }
          if (!persistentState.endMonth) {
            const currentMonth = getCurrentMonth();
            setEndMonth(currentMonth);
            persistentState.endMonth = currentMonth;
          }
          if (!persistentState.financialYear) {
            const currentFY = getCurrentFinancialYear();
            setFinancialYear(currentFY);
            persistentState.financialYear = currentFY;
          }

          persistentState.isInitialized = true;
        } catch (err) {
          const updatedStates = [{ id: "All", name: "All" }];
          setStates(updatedStates);
          persistentState.states = updatedStates;
        }
      } else {
        // Restore from persistent storage
        setStates(
          persistentState.states.length
            ? persistentState.states
            : [{ id: "All", name: "All" }],
        );
        setDivisions(
          persistentState.divisions && persistentState.divisions.length
            ? persistentState.divisions
            : [{ id: "All", name: "All" }],
        );
        setEmployees(
          persistentState.employees && persistentState.employees.length
            ? persistentState.employees
            : [{ id: "All", first_name: "All", last_name: "" }],
        );
        setSelectedState(persistentState.selectedState || "All");
        setSelectedDivision(persistentState.selectedDivision || "All");
        setSelectedEmployee(persistentState.selectedEmployee || "All");
        setRetailerType(persistentState.retailerType || "All");
        setActiveStatus(persistentState.activeStatus || "All");
        setStartMonth(persistentState.startMonth || getCurrentMonth());
        setEndMonth(persistentState.endMonth || getCurrentMonth());
        setFinancialYear(
          persistentState.financialYear || getCurrentFinancialYear(),
        );
        setTableData(persistentState.tableData || []);
        setShowTable(!!persistentState.showTable);
      }
    };
    fetchStates();
  }, []);

  // Update persistent storage whenever state changes
  useEffect(() => {
    persistentState.states = states;
    persistentState.divisions = divisions;
    persistentState.employees = employees;
    persistentState.selectedState = selectedState;
    persistentState.selectedDivision = selectedDivision;
    persistentState.selectedEmployee = selectedEmployee;
    persistentState.retailerType = retailerType;
    persistentState.activeStatus = activeStatus;
    persistentState.startMonth = startMonth;
    persistentState.endMonth = endMonth;
    persistentState.financialYear = financialYear;
    persistentState.tableData = tableData;
    persistentState.showTable = showTable;
  }, [
    states,
    divisions,
    employees,
    selectedState,
    selectedDivision,
    selectedEmployee,
    retailerType,
    activeStatus,
    startMonth,
    endMonth,
    financialYear,
    tableData,
    showTable,
  ]);

  // Handle state change
  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    persistentState.selectedState = stateId;

    // Reset division and employee when state changes
    setSelectedDivision("All");
    persistentState.selectedDivision = "All";
    setSelectedEmployee("All");
    persistentState.selectedEmployee = "All";

    if (!stateId || stateId === "All") {
      const updatedDivisions = [{ id: "All", name: "All" }];
      setDivisions(updatedDivisions);
      persistentState.divisions = updatedDivisions;

      const updatedEmployees = [
        { id: "All", first_name: "All", last_name: "" },
      ];
      setEmployees(updatedEmployees);
      persistentState.employees = updatedEmployees;
      return;
    }

    try {
      const divisionRes = await axios.get(
        `${BASE_URL}/api/divisions?state_id=${stateId}`,
        { headers: getAuthHeaders() },
      );
      const updatedDivisions = [
        { id: "All", name: "All" },
        ...divisionRes.data,
      ];
      setDivisions(updatedDivisions);
      persistentState.divisions = updatedDivisions;
    } catch (err) {
      const updatedDivisions = [{ id: "All", name: "All" }];
      setDivisions(updatedDivisions);
      persistentState.divisions = updatedDivisions;
    }

    fetchEmployees(stateId, "All");
  };

  // Handle division change
  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    persistentState.selectedDivision = divisionId;

    // Reset employee when division changes
    setSelectedEmployee("All");
    persistentState.selectedEmployee = "All";

    const division = divisions.find((d) => String(d.id) === String(divisionId));
    const divisionName = division?.name || "All";
    fetchEmployees(selectedState, divisionName);
  };

  // Format month for API
  const formatMonthForAPI = (monthString) => {
    if (!monthString) return "";
    const [year, month] = monthString.split("-");
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const monthIndex = parseInt(month, 10) - 1;
    return `${monthNames[monthIndex]} ${year}`;
  };

  // Handle GO button click
  const handleGoClick = async () => {
    // Validate date range
    if (startMonth && endMonth && startMonth > endMonth) {
      toast.warning("Start month cannot be after end month");
      return;
    }

    setErrors({ state: false, employee: false });
    setLoading(true);
    setShowTable(true);
    persistentState.showTable = true;

    try {
      let url = `${BASE_URL}/api/combined-retailers`;
      const params = new URLSearchParams();

      // Add filters only if not "All"
      if (selectedState && selectedState !== "All") {
        params.append("state_id", selectedState);
      }

      if (selectedDivision && selectedDivision !== "All") {
        const division = divisions.find(
          (d) => String(d.id) === String(selectedDivision),
        );
        if (division && division.name !== "All") {
          params.append("division", division.name);
        }
      }

      if (selectedEmployee && selectedEmployee !== "All") {
        params.append("employee_id", selectedEmployee);
      }

      if (retailerType && retailerType !== "All") {
        params.append("retailer_type", retailerType.toLowerCase());
      }

      if (activeStatus && activeStatus !== "All") {
        params.append("active", activeStatus === "Yes" ? "true" : "false");
      }

      const formattedStartMonth = formatMonthForAPI(startMonth);
      const formattedEndMonth = formatMonthForAPI(endMonth);

      if (formattedStartMonth)
        params.append("start_month", formattedStartMonth);
      if (formattedEndMonth) params.append("end_month", formattedEndMonth);

      if (financialYear && financialYear !== "All") {
        params.append("financial_year", financialYear);
      }

      const finalUrl = params.toString() ? `${url}?${params.toString()}` : url;

      const response = await axios.get(finalUrl, { headers: getAuthHeaders() });
      setTableData(response.data || []);
      persistentState.tableData = response.data || [];
    } catch (error) {
      toast.error("Error fetching data. Please try again.");
      setTableData([]);
      persistentState.tableData = [];
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const day = date.getDate().toString().padStart(2, "0");
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (err) {
      return "";
    }
  };

  // Edit functionality
  const handleEditClick = (index, item) => {
    setEditingRow(index);
    setEditStatus(item.active);
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditStatus(false);
  };

  const handleSaveStatus = async (item) => {
    const type = (item.type || "").toLowerCase();

    if (!["doctor", "chemist"].includes(type)) {
      toast.warning("Status update is only available for Doctors and Chemists");
      return;
    }

    setIsSaving(true);
    try {
      if (type === "doctor") {
        const payload = {
          employee_ids: [item.employee_id || item.emp_id],
          is_active: editStatus,
        };

        const response = await axios.put(
          `${BASE_URL}/api/doctors/deactivate`,
          payload,
          { headers: getAuthHeaders() },
        );

        if (response.status === 200) {
          const updatedData = tableData.map((row, idx) =>
            idx === editingRow ? { ...row, active: editStatus } : row,
          );
          setTableData(updatedData);
          persistentState.tableData = updatedData;
          setEditingRow(null);
          toast.success("Doctor status updated successfully");
        } else {
          throw new Error("Unexpected response from doctor API");
        }
      } else if (type === "chemist") {
        const chemistCode = item.code || item.chemist_code || item.id;
        if (!chemistCode) {
          throw new Error("Chemist code not found");
        }

        const payload = { is_active: !!editStatus };

        const response = await axios.put(
          `${BASE_URL}/api/chemist/chemist/${encodeURIComponent(
            chemistCode,
          )}/status`,
          payload,
          { headers: getAuthHeaders() },
        );

        if (response.status === 200 || response.status === 204) {
          const updatedData = tableData.map((row, idx) =>
            idx === editingRow ? { ...row, active: editStatus } : row,
          );
          setTableData(updatedData);
          persistentState.tableData = updatedData;
          setEditingRow(null);
          toast.success("Chemist status updated successfully");
        } else {
          throw new Error("Unexpected response from chemist API");
        }
      }
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle total POB click -> open popup instead of navigate
  const handleTotalPobClick = (item) => {
    const empId = item.employee_id || item.emp_id || selectedEmployee;
    const type = (item.type || "").toLowerCase();

    if (!empId || empId === "All") {
      toast.warning("Employee information not available");
      return;
    }

    const queryParams = new URLSearchParams({
      viewType: "total",
      retailerId: item.id || item.code,
      type: type,
    });

    const url = `/pob-details-doctor-chemists/${empId}?${queryParams.toString()}`;
    openPopup(url, { width: 900, height: 680 });
  };

  // Handle month POB click -> open popup
  const handleMonthPobClick = (item, monthKey, monthLabel) => {
    const empId = item.employee_id || item.emp_id || selectedEmployee;
    const type = (item.type || "").toLowerCase();

    if (!empId || empId === "All") {
      toast.warning("Employee information not available");
      return;
    }

    // Determine year based on month
    const [startYear] = (financialYear || getCurrentFinancialYear()).split("-");
    const monthIndex = monthKeys.indexOf(monthKey);

    const year =
      monthIndex <= 8 ? parseInt(startYear) : parseInt(startYear) + 1;

    const queryParams = new URLSearchParams({
      monthKey: monthKey,
      year: year.toString(),
      type: type,
      retailerId: item.id || item.code,
    });

    const url = `/pob-details-doctor-chemists/${empId}?${queryParams.toString()}`;
    openPopup(url, { width: 900, height: 680 });
  };

  // Handle route click -> open popup
  const handleRouteClick = (item) => {
    const retailerId = item.id || item.code;
    const empId = item.employee_id || item.emp_id || "";
    const type = (item.type || "").toLowerCase();

    const queryParams = new URLSearchParams({
      retailerId: retailerId,
      employee_id: empId,
      retailer_type: type,
    });

    const url = `/route-details?${queryParams.toString()}`;
    openPopup(url, { width: 900, height: 680 });
  };

  // Print functionality
  const handlePrint = () => {
    if (tableData.length === 0) {
      toast.info("No data to print");
      return;
    }

    // get the actual table DOM
    const tableEl = document.querySelector("table");

    if (!tableEl) {
      toast.error("Unable to find table for printing");
      return;
    }

    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Visit/Support Report</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              font-size: 12px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              border: 1px solid #000;
              padding: 6px;
              white-space: nowrap;
              font-size: 12px;
            }
            th {
              background-color: #f0f0f0;
              font-weight: bold;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            @media print {
              body { margin: 0; }
              button { display:none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Visit/Support Report</h2>
            <p>${selectedEmployee !== "All"
        ? `Employee: ${employees.find(
          (e) => String(e.id) === String(selectedEmployee),
        )?.first_name || ""
        } ${employees.find(
          (e) => String(e.id) === String(selectedEmployee),
        )?.last_name || ""
        }`
        : "All Employees"
      }</p>
            <p>Period: ${formatMonthForAPI(startMonth) || ""} to ${formatMonthForAPI(endMonth) || "N/A"
      }</p>
          </div>

          ${tableEl.outerHTML}

          <p style="text-align:center; margin-top:20px; font-size:10px;">
            Generated on: ${new Date().toLocaleString()}
          </p>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

 const handleExportExcel = () => {
  if (tableData.length === 0) {
    toast.info("No data to export");
    return;
  }

  try {
    const baseHeaders = [
      "S.No", "Employee", "Type", "Code", "Retailer", "Active",
      "State", "City", "Area", "Route", "Mobile", "Address",
      "System Added Date", "Latest Visit Date",
      "Prev FY Avg", "Curr FY Avg", "Total Visits", "Total POB"
    ];

    const monthHeaders = visibleMonthDisplay.flatMap((month) => [
      `${month} Visits`,
      `${month} POB`
    ]);

    const headers = [...baseHeaders, ...monthHeaders];

    // ✅ Use null placeholder for date columns (col 12 & 13), patch after sheet creation
    const rows = tableData.map((item, index) => {
      const baseRow = [
        index + 1,
        item.employee_name || "",
        item.type || "",
        item.code || "",
        item.name || "",
        item.active ? "Yes" : "No",
        item.state_name || "",
        item.city_name || "",
        item.area_name || "",
        item.route_name || "",
        item.mobile || "",
        item.address || "",
        null,  // ← System Added Date (patched below)
        null,  // ← Latest Visit Date (patched below)
        Number(item.fy_avg_prev || 0),
        Number(item.fy_avg_curr || 0),
        visibleMonthKeys.reduce((sum, m) => sum + Number(item[`${m}_visit`] || 0), 0),
        visibleMonthKeys.reduce((sum, m) => sum + Number(item[`${m}_pob`] || 0), 0),
      ];

      const monthData = visibleMonthKeys.flatMap((m) => [
        Number(item[`${m}_visit`] || 0),
        Number(item[`${m}_pob`] || 0),
      ]);

      return [...baseRow, ...monthData];
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const range = XLSX.utils.decode_range(ws["!ref"]);

    // ✅ Patch System Added Date (col 12) and Latest Visit Date (col 13) as real Excel dates
    const DATE_COLS = {
      12: tableData.map(item => item.system_added_date),
      13: tableData.map(item => item.latest_visit_date),
    };

    Object.entries(DATE_COLS).forEach(([colIndex, values]) => {
      values.forEach((rawVal, rowIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: Number(colIndex) });
        if (rawVal) {
          try {
            const date = new Date(
              /^\d{4}-\d{2}-\d{2}$/.test(String(rawVal))
                ? `${rawVal}T00:00:00`
                : rawVal
            );
            if (!isNaN(date.getTime())) {
              ws[cellAddress] = {
                v: date,          // ✅ Real Excel date
                t: "d",           // ✅ Date type
                z: "dd-mmm-yyyy", // ✅ Excel date format
              };
              return;
            }
          } catch {}
        }
        ws[cellAddress] = { v: "", t: "s" };
      });
    });

    // Styles
    const borderStyle = {
      top: { style: "thin", color: { rgb: "FF000000" } },
      bottom: { style: "thin", color: { rgb: "FF000000" } },
      left: { style: "thin", color: { rgb: "FF000000" } },
      right: { style: "thin", color: { rgb: "FF000000" } },
    };

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFFFF" } },
      fill: { fgColor: { rgb: "FF2563EB" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: borderStyle,
    };

    const rowStyle = {
      alignment: { horizontal: "center", vertical: "center" },
      border: borderStyle,
    };

    // Apply styles to all cells
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[addr]) ws[addr] = { t: "z", v: "" };
        ws[addr].s = R === 0 ? headerStyle : rowStyle;
      }
    }

    ws["!cols"] = headers.map(() => ({ wch: 18 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RRR Report");
    XLSX.writeFile(wb, `RRR_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);

  } catch (error) {
    console.error(error);
    toast.error("Export failed");
  }
};
  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      {loading && <Loader onCancel={() => setLoading(false)} />}
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">RRR </span>
          <span className="text-blue-500">Report</span>
        </h1>

        <div className="msl_pob-row1">
          <label className="msl-pob-label">State</label>
          <select
            className="msl-pob-dropdown"
            onChange={(e) => {
              handleStateChange(e.target.value);
              setErrors({ ...errors, state: false });
            }}
            value={selectedState}
          >
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="msl-pob-label">Division</label>
          <select
            className="msl-pob-dropdown"
            onChange={(e) => handleDivisionChange(e.target.value)}
            value={selectedDivision}
          >
            {divisions.map((div) => (
              <option key={div.id} value={div.id}>
                {div.name}
              </option>
            ))}
          </select>
        </div>

        <div className="msl_pob-row2">
          <label className="msl-pob-label">Employee</label>
          <select
            className="msl-pob-dropdown"
            onChange={(e) => {
              setSelectedEmployee(e.target.value);
              persistentState.selectedEmployee = e.target.value;
              setErrors({ ...errors, employee: false });
            }}
            value={selectedEmployee}
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.id === "All"
                  ? "All"
                  : `${emp.first_name} ${emp.last_name} ${emp.employee} | ${emp.designation || ""
                  } | ${emp.headquarter_name || ""} | ${emp.division || ""} | ${emp.state_name || ""
                  }`}
              </option>
            ))}
          </select>

          <label className="msl-pob-label">Retailer Type</label>
          <select
            className="msl-pob-dropdown"
            value={retailerType}
            onChange={(e) => {
              setRetailerType(e.target.value);
              persistentState.retailerType = e.target.value;
            }}
          >
            <option value="All">All</option>
            <option value="Doctor">Doctor</option>
            <option value="Chemist">Chemist</option>
          </select>
        </div>

        <div className="msl_pob-row3">
          <label className="msl-pob-label">FY Start Month</label>
          <input
            className="month-input"
            type="month"
            value={startMonth}
            onChange={(e) => {
              setStartMonth(e.target.value);
              persistentState.startMonth = e.target.value;
            }}
          />

          <label className="msl-pob-label">FY End Month</label>
          <input
            className="month-input"
            type="month"
            value={endMonth}
            onChange={(e) => {
              setEndMonth(e.target.value);
              persistentState.endMonth = e.target.value;
            }}
          />
        </div>

        <div className="msl_pob-row3">
          <label className="msl-pob-label">Financial Year</label>
          <select
            className="msl-pob-dropdown"
            value={financialYear}
            onChange={(e) => {
              setFinancialYear(e.target.value);
              persistentState.financialYear = e.target.value;
            }}
          >
            {getFinancialYears().map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <label className="msl-pob-label">Active</label>
          <select
            className="msl-pob-dropdown"
            value={activeStatus}
            onChange={(e) => {
              setActiveStatus(e.target.value);
              persistentState.activeStatus = e.target.value;
            }}
          >
            <option value="All">All</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>

        <div className="flex items-center justify-center">
          <CustomButton variant="go" onClick={handleGoClick} disabled={loading}>
            {loading ? "Loading..." : "GO"}
          </CustomButton>
        </div>
      </div>

      {showTable && (
        <>
          <div className="border rounded shadow  table-wrapper mt-7">
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th>S.No</th>
                  <th>Action</th>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Code</th>
                  <th>Retailer</th>
                  <th>Active</th>
                  <th>State</th>
                  <th>City</th>
                  <th>Area</th>
                  <th>Route</th>
                  <th>Mobile</th>
                  <th>Address</th>
                  <th>System Added Date</th>
                  <th>Latest Visit Date</th>
                  <th>Prev FY Avg</th>
                  <th>Curr FY Avg</th>
                  <th>Total Visits</th>
                  <th>Total POB</th>

                  {visibleMonthDisplay.map((month, i) => (
                    <React.Fragment key={month}>
                      <th>{month} Visits</th>
                      <th>{month} POB</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{index + 1}</td>

                    {/* Action column */}
                    <td>
                      {editingRow === index ? (
                        <div className="action-buttons">
                          <CustomButton
                            label={isSaving ? "Saving..." : "Save"}
                            onClick={() => handleSaveStatus(item)}
                            disabled={isSaving}
                            variant="save"
                          />
                          <CustomButton
                            label="Cancel"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                            variant="cancel"
                          />
                        </div>
                      ) : (
                        <CustomButton
                          label="Edit"
                          onClick={() => handleEditClick(index, item)}
                          disabled={
                            !["doctor", "chemist"].includes(
                              (item.type || "").toLowerCase(),
                            )
                          }
                          variant="edit"
                          title={
                            !["doctor", "chemist"].includes(
                              (item.type || "").toLowerCase(),
                            )
                              ? "Edit available only for Doctors and Chemists"
                              : "Edit Status"
                          }
                        />
                      )}
                    </td>

                    <td>{item.employee_name || ""}</td>
                    <td>{item.type || ""}</td>
                    <td>{item.code || ""}</td>
                    <td>{item.name || ""}</td>

                    <td>
                      {editingRow === index ? (
                        <div className="status-edit-container">
                          <label>
                            <input
                              type="radio"
                              name={`status-${index}`}
                              value="true"
                              checked={editStatus === true}
                              onChange={() => setEditStatus(true)}
                            />
                            Yes
                          </label>
                          <label>
                            <input
                              type="radio"
                              name={`status-${index}`}
                              value="false"
                              checked={editStatus === false}
                              onChange={() => setEditStatus(false)}
                            />
                            No
                          </label>
                        </div>
                      ) : item.active ? (
                        "Yes"
                      ) : (
                        "No"
                      )}
                    </td>

                    <td>{item.state_name || ""}</td>
                    <td>{item.city_name || ""}</td>
                    <td>{item.area_name || ""}</td>

                    <td>
                      <button
                        type="button"
                        className="msl_link_button"
                        onClick={() => handleRouteClick(item)}
                      >
                        {item.route_name || "View Route"}
                      </button>
                    </td>

                    <td>{item.mobile || ""}</td>
                    <td className="fixed-address">{item.address || ""}</td>
                    <td>{formatDate(item.system_added_date)}</td>
                    <td>{formatDate(item.latest_visit_date)}</td>
                    <td>{item.fy_avg_prev || 0}</td>
                    <td>{item.fy_avg_curr || 0}</td>
                    <td>
                      {visibleMonthKeys.reduce(
                        (sum, m) => sum + Number(item[`${m}_visit`] || 0),
                        0,
                      )}
                    </td>

                    {/* Total POB clickable */}
                    <td>
                      <button
                        type="button"
                        className="msl_link_button"
                        onClick={() => handleTotalPobClick(item)}
                      >
                        {visibleMonthKeys.reduce(
                          (sum, m) => sum + Number(item[`${m}_pob`] || 0),
                          0,
                        )}
                      </button>
                    </td>

                    {/* Month-wise visits and clickable month POB */}
                    {visibleMonthKeys.map((mkey) => (
                      <React.Fragment key={mkey}>
                        <td>{Number(item[`${mkey}_visit`] || 0)}</td>
                        <td>
                          <button
                            type="button"
                            className="msl_link_button"
                            onClick={() =>
                              handleMonthPobClick(
                                item,
                                mkey,
                                monthDisplay[monthKeys.indexOf(mkey)],
                              )
                            }
                          >
                            {Number(item[`${mkey}_pob`] || 0)}
                          </button>
                        </td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
              {footerTotals && (
                <tfoot className="bg-blue-600 font-bold text-white">
                  <tr>
                    <td colSpan="13" className="text-right pr-2">
                      Grand Total
                    </td>
                    <td></td>
                    <td></td>
                    <td>{footerTotals.prevFyAvg}</td>
                    <td>{footerTotals.currFyAvg}</td>
                    <td>{footerTotals.totalVisits}</td>
                    <td>{footerTotals.totalPob}</td>

                    {visibleMonthKeys.map((m) => (
                      <React.Fragment key={`footer-${m}`}>
                        <td>{footerTotals.monthTotals[m].visit}</td>
                        <td>{footerTotals.monthTotals[m].pob}</td>
                      </React.Fragment>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {!loading && tableData.length > 0 && (
            <div className="flex gap-2 justify-center mt-2">
              <CustomButton onClick={handlePrint} label="Print" variant="print">
                🖨
              </CustomButton>

              <CustomButton
                onClick={handleExportExcel}
                label="Export"
                variant="export"
              >
                📥
              </CustomButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RrrReports;
